import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import { useSkinCatalog } from './catalog.js'
import { FALLBACK_SKIN, assetUrl, useSkin } from './skin.js'
import type { Skin } from './skin.js'

const SkinContext = createContext<Skin>(FALLBACK_SKIN)

/** Loads the lobby-wide skin and makes its assets reachable from any component. */
export function SkinProvider({ skinId, children }: { skinId: string | null | undefined; children: ReactNode }) {
  const skin = useSkin(skinId)
  return <SkinContext.Provider value={skin}>{children}</SkinContext.Provider>
}

/**
 * The skin a new lobby starts on, for the pages that belong to no lobby - the
 * rules, the tour - so their tiles and cards are the ones the table will see.
 * Nothing at all on a deployment without skins, as there.
 */
export function DefaultSkinProvider({ children }: { children: ReactNode }) {
  const catalog = useSkinCatalog()
  return <SkinProvider skinId={catalog.skins.length > 0 ? catalog.default : null}>{children}</SkinProvider>
}

export function useSkinValue(): Skin {
  return useContext(SkinContext)
}

/**
 * The URL for a piece of skin artwork, or null when the active skin has none.
 *
 * Artwork is always decoration layered over the token styling, never a
 * replacement for it: a skin with no assets must render an identical, fully
 * readable board.
 */
export function useAsset(key: string): string | null {
  return assetUrl(useContext(SkinContext), key)
}
